import { useEffect, useRef, useState } from 'react';
import {
  DndContext,
  DragOverlay,
  pointerWithin,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragStartEvent,
  DragEndEvent,
} from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { ArrowRight } from 'lucide-react';
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import { Button } from '@/components/ui/button';
import { ClientPipelineColumn } from './ClientPipelineColumn';
import { PausedRail } from './PausedRail';
import { ClientCard } from './ClientCard';
import { Client, ClientLifecycleStage, LIFECYCLE_STAGES } from '@/types/crm';

interface ClientPipelineBoardProps {
  clients: Client[];
  onClientClick?: (client: Client) => void;
  onMoveClient: (client: Client, toStage: ClientLifecycleStage) => Promise<void>;
  savingClientId?: string | null;
  isBulkMode?: boolean;
  selectedClientIds?: Set<string>;
  onToggleSelect?: (clientId: string) => void;
}

// Order stages for display - show active pipeline stages only (not churned/paused in main board)
const STAGE_ORDER: ClientLifecycleStage[] = ['lead', 'opportunity', 'interest', 'deck_sent', 'closing', 'committed', 'active'];

// Stages a card can be dropped onto: the pipeline plus the paused lane.
const DROPPABLE_STAGES: ClientLifecycleStage[] = [...STAGE_ORDER, 'paused'];

const MOBILE_STAGE_KEY = 'crm-board-mobile-stage';
const PAUSED_OPEN_KEY = 'crm-board-paused-open';

export function ClientPipelineBoard({
  clients,
  onClientClick,
  onMoveClient,
  savingClientId,
  isBulkMode,
  selectedClientIds,
  onToggleSelect,
}: ClientPipelineBoardProps) {
  const [activeClient, setActiveClient] = useState<Client | null>(null);
  const [isPausedOpen, setIsPausedOpen] = useState(
    () => sessionStorage.getItem(PAUSED_OPEN_KEY) === 'true'
  );
  const [quickMoveClient, setQuickMoveClient] = useState<Client | null>(null);
  const [activeStageIdx, setActiveStageIdx] = useState(() => {
    const saved = sessionStorage.getItem(MOBILE_STAGE_KEY);
    const idx = saved ? STAGE_ORDER.indexOf(saved as ClientLifecycleStage) : -1;
    return idx >= 0 ? idx : 0;
  });
  const scrollerRef = useRef<HTMLDivElement>(null);
  const scrollRaf = useRef<number>(0);

  // Sensors are always registered (changing the array size between renders breaks
  // dnd-kit's internal hooks); bulk mode disables dragging per-card instead.
  // MouseSensor (not PointerSensor) is paired with TouchSensor deliberately:
  // pointerdown fires before touchstart on touch devices, so a PointerSensor
  // would win the activation race on its 8px distance constraint before
  // TouchSensor's long-press delay ever gets a chance, hijacking scroll
  // gestures into drags. Mouse events don't fire once TouchSensor claims a
  // touch interaction, so this pairing is dnd-kit's documented mixed-input path.
  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    // Touch devices: press and hold ~250ms to lift a card, so vertical scrolling
    // and taps keep working naturally.
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 250,
        tolerance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Group clients by lifecycle stage
  const clientsByStage = DROPPABLE_STAGES.reduce((acc, stage) => {
    acc[stage] = clients.filter(c => c.lifecycle_stage === stage);
    return acc;
  }, {} as Record<ClientLifecycleStage, Client[]>);

  // Restore last-viewed column on mount (phone carousel only; no-op when the
  // container isn't horizontally scrollable).
  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller || scroller.scrollWidth <= scroller.clientWidth) return;
    const col = scroller.children[activeStageIdx] as HTMLElement | undefined;
    if (col && activeStageIdx > 0) {
      scroller.scrollLeft = col.offsetLeft - (scroller.clientWidth - col.offsetWidth) / 2;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleBoardScroll = () => {
    cancelAnimationFrame(scrollRaf.current);
    scrollRaf.current = requestAnimationFrame(() => {
      const scroller = scrollerRef.current;
      if (!scroller || scroller.scrollWidth <= scroller.clientWidth) return;
      const center = scroller.scrollLeft + scroller.clientWidth / 2;
      let closest = 0;
      let closestDist = Infinity;
      Array.from(scroller.children).forEach((child, i) => {
        const el = child as HTMLElement;
        const mid = el.offsetLeft + el.offsetWidth / 2;
        const dist = Math.abs(mid - center);
        if (dist < closestDist) {
          closestDist = dist;
          closest = i;
        }
      });
      if (closest !== activeStageIdx) {
        setActiveStageIdx(closest);
        sessionStorage.setItem(MOBILE_STAGE_KEY, STAGE_ORDER[closest]);
      }
    });
  };

  const scrollToStage = (idx: number) => {
    const scroller = scrollerRef.current;
    const col = scroller?.children[idx] as HTMLElement | undefined;
    if (!scroller || !col) return;
    scroller.scrollTo({
      left: col.offsetLeft - (scroller.clientWidth - col.offsetWidth) / 2,
      behavior: 'smooth',
    });
    setActiveStageIdx(idx);
    sessionStorage.setItem(MOBILE_STAGE_KEY, STAGE_ORDER[idx]);
  };

  const handleDragStart = (event: DragStartEvent) => {
    const client = clients.find(c => c.id === event.active.id);
    setActiveClient(client || null);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveClient(null);

    if (!over) return;

    const clientId = active.id as string;

    // Determine the target stage - over.id could be a column (stage) or another card (client ID)
    let newStage: ClientLifecycleStage;

    if (DROPPABLE_STAGES.includes(over.id as ClientLifecycleStage)) {
      // Dropped on a column
      newStage = over.id as ClientLifecycleStage;
    } else {
      // Dropped on a card - find which stage that card belongs to
      const targetClient = clients.find(c => c.id === over.id);
      if (!targetClient) return;
      newStage = targetClient.lifecycle_stage;
    }

    // Find the client being dragged
    const client = clients.find(c => c.id === clientId);
    if (!client) return;

    await onMoveClient(client, newStage);
  };

  const handleDragCancel = () => {
    // Escape, a focus change, or any other interruption ends the drag without
    // an onDragEnd — without this, activeClient stays truthy and permanently
    // suppresses snap-scrolling on the phone carousel (see the className below).
    setActiveClient(null);
  };

  const setPausedOpen = (open: boolean) => {
    setIsPausedOpen(open);
    sessionStorage.setItem(PAUSED_OPEN_KEY, String(open));
  };

  const handleQuickMove = async (toStage: ClientLifecycleStage) => {
    const client = quickMoveClient;
    setQuickMoveClient(null);
    if (client) await onMoveClient(client, toStage);
  };

  return (
    <>
      {/* Phone-only stage navigator: shows the shape of the pipeline at a glance
          and jumps the carousel between columns */}
      <div className="sm:hidden flex gap-2 overflow-x-auto -mx-4 px-4 pb-1 [-webkit-overflow-scrolling:touch] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        <button
          type="button"
          onClick={() => setPausedOpen(!isPausedOpen)}
          aria-expanded={isPausedOpen}
          className={`h-11 shrink-0 rounded-full border px-4 text-xs font-medium transition-colors ${
            isPausedOpen
              ? 'bg-primary text-primary-foreground border-primary'
              : 'bg-card text-muted-foreground border-border'
          }`}
        >
          {LIFECYCLE_STAGES.paused.label}
          <span className={`ml-1.5 ${isPausedOpen ? 'opacity-80' : 'opacity-60'}`}>
            {clientsByStage.paused.length}
          </span>
        </button>
        {STAGE_ORDER.map((stage, i) => (
          <button
            key={stage}
            type="button"
            onClick={() => scrollToStage(i)}
            aria-current={activeStageIdx === i ? 'true' : undefined}
            className={`h-11 shrink-0 rounded-full border px-4 text-xs font-medium transition-colors ${
              activeStageIdx === i
                ? 'bg-primary text-primary-foreground border-primary'
                : 'bg-card text-muted-foreground border-border'
            }`}
          >
            {LIFECYCLE_STAGES[stage].label}
            <span className={`ml-1.5 ${activeStageIdx === i ? 'opacity-80' : 'opacity-60'}`}>
              {clientsByStage[stage].length}
            </span>
          </button>
        ))}
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={pointerWithin}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        {/* One board for every width: horizontally snap-scrolling carousel on
            phones, responsive grid at sm+ (snap disabled mid-drag so dnd-kit's
            edge auto-scroll isn't fighting the snap points) */}
        <div
          className={`
            flex gap-3 items-stretch overflow-x-auto -mx-4 px-4 pb-4 scroll-px-4
            ${activeClient ? '' : 'snap-x snap-mandatory'}
            sm:mx-0 sm:px-0 sm:overflow-visible sm:pb-0
          `}
        >
          <PausedRail
            clients={clientsByStage.paused}
            isOpen={isPausedOpen}
            onOpenChange={setPausedOpen}
            onClientClick={onClientClick}
            onQuickMove={isBulkMode ? undefined : setQuickMoveClient}
            savingClientId={savingClientId}
            isBulkMode={isBulkMode}
            selectedClientIds={selectedClientIds}
            onToggleSelect={onToggleSelect}
          />

          <div
            ref={scrollerRef}
            onScroll={handleBoardScroll}
            className={`
              flex gap-3 min-w-0 flex-1 overflow-x-auto pb-4 scroll-px-4
              ${activeClient ? '' : 'snap-x snap-mandatory'}
              sm:overflow-visible
              sm:grid sm:grid-cols-2
              md:grid-cols-3
              lg:grid-cols-4
              xl:grid-cols-6
            `}
          >
            {STAGE_ORDER.map((stage) => (
              <ClientPipelineColumn
                key={stage}
                stage={stage}
                clients={clientsByStage[stage]}
                onClientClick={onClientClick}
                onQuickMove={isBulkMode ? undefined : setQuickMoveClient}
                savingClientId={savingClientId}
                isBulkMode={isBulkMode}
                selectedClientIds={selectedClientIds}
                onToggleSelect={onToggleSelect}
              />
            ))}
          </div>
        </div>

        <DragOverlay dropAnimation={{ duration: 250, easing: 'cubic-bezier(0.18, 0.67, 0.6, 1.22)' }}>
          {activeClient && (
            <div className="rotate-[2deg] scale-105">
              <ClientCard client={activeClient} />
            </div>
          )}
        </DragOverlay>
      </DndContext>

      {/* Quick-move: one-tap stage change without dragging (phone affordance) */}
      <Drawer
        open={!!quickMoveClient}
        onOpenChange={(open) => { if (!open) setQuickMoveClient(null); }}
        direction="bottom"
      >
        <DrawerContent className="max-h-[85vh]">
          <DrawerHeader className="px-4 pt-4 pb-2">
            <DrawerTitle>{quickMoveClient?.company_name}</DrawerTitle>
            <DrawerDescription>
              Move to stage
              {quickMoveClient && (
                <> — currently {LIFECYCLE_STAGES[quickMoveClient.lifecycle_stage].label}</>
              )}
            </DrawerDescription>
          </DrawerHeader>
          <div className="grid gap-2 overflow-y-auto px-4 pb-[calc(1rem+env(safe-area-inset-bottom))]">
            {DROPPABLE_STAGES.map((stage) => (
              <Button
                key={stage}
                variant="outline"
                className="justify-start h-auto min-h-11 py-3"
                disabled={quickMoveClient?.lifecycle_stage === stage}
                onClick={() => handleQuickMove(stage)}
              >
                <ArrowRight className="h-4 w-4 mr-2 flex-shrink-0" />
                <div className="text-left">
                  <div>{LIFECYCLE_STAGES[stage].label}</div>
                  <div className="text-muted-foreground text-xs font-normal">
                    {LIFECYCLE_STAGES[stage].description}
                  </div>
                </div>
              </Button>
            ))}
          </div>
        </DrawerContent>
      </Drawer>
    </>
  );
}
